import React, { useState, useRef, useEffect } from "react";
import { useNavigate } from 'react-router-dom';
import TenderProcessTable from "../TenderProcessTable/TenderProcessTable";
import SubStepTracker from "../../components/SubStepTracker/SubStepTracker";
import { 
    importFinalizedSummary,
    processSummarySheetPdf,
    exportSummarySheetPdf,
    reselectFinalizedSummarySheet,
    validateFinalizedSummary,
    cachedValidationResults
} from "../../api/api";
import { 
    ImportButton,
    Header,
    SubHeader,
    SourceDocumentsPageContainer,
    GenerateButton,
    ReselectButton,
    ButtonContainer
} from "../TenderProcess/TenderProcess.styles";
import { useTenderFiles } from "./useTenderFiles";
import { useToast } from '../ToastProvider/ToastProvider';
import Validations from './Validations'

const UploadSourceDocuments = ({ handleFileUpload }) => {
    const fileInputRef = useRef(null);

    const handleImportClick = () => {
        fileInputRef.current.click();
    };

    const handleFileChange = (event) => {
        const files = Array.from(event.target.files);
        if (files.length > 0) {
            handleFileUpload(files);
        }
    };

    return (
        <>
            <ImportButton data-testid="import-button" onClick={handleImportClick}>
                Import
            </ImportButton>
            <input
                data-testid="file-selector"
                ref={fileInputRef}
                type="file"
                accept=".xlsm, .xlsx"
                style={{ display: "none" }}
                onChange={handleFileChange}
            />
        </>
    );
};

const ReviewFiles = ({ handleReselectFiles, handleProcessSpreadsheet, files }) => {
    return (
        <>
            <TenderProcessTable files={files}/>
            <ButtonContainer>
                <ReselectButton data-testid="reselect-button" onClick={handleReselectFiles}>Reselect Files</ReselectButton>
                <GenerateButton data-testid="generate-button" onClick={handleProcessSpreadsheet}>Generate</GenerateButton>
            </ButtonContainer>
        </>
    );
};

const SaveSummarySpreadsheet = ({ handleExportSpreadsheet }) => {
    return (
        <>
            <ImportButton data-testid="save-to-sharepoint-button" onClick={handleExportSpreadsheet}>
                Save File
            </ImportButton>
        </>
    );
};

const ValidationReview = ({ handleContinue, handleReselect, validationData }) => {
    return (
        <>
            <Validations validationData={validationData} />
            <ButtonContainer>
                <ReselectButton data-testid="reselect-button" onClick={handleReselect}>Reselect Files</ReselectButton>
                <GenerateButton data-testid="continue-button" onClick={handleContinue}>Continue</GenerateButton>
            </ButtonContainer>
        </>
    );
};

const mapSubSteptoNumber = (subStep) => {
    if (subStep == null) return 1;
    return {
        'upload': 1,
        'validate': 2,
        'validate-sheet': 2,
        'process': 3,
        'generate': 4
    }[subStep]
}

const FinalizedSummary = ({tenderId, subStep}) => {
    const navigate = useNavigate();
    const { showToast } = useToast();
    const [step, setStep] = useState(mapSubSteptoNumber(subStep));
    const [loading, setLoading] = useState(false);
    const [validationData, setValidationData] = useState([]);
    const { files, setFiles } = useTenderFiles({ tenderId, step, setLoading });

    const transformValidationData = (backendValidations) => {
        const transformed = [];
        
        backendValidations.forEach(categoryData => {
            const { category, results } = categoryData;
            
            results.forEach(result => {
                transformed.push({
                    category: category,
                    field_name: result.field_name,
                    tab: result.tab,
                    row: result.line,
                    rule: result.rule,
                    passed: result.valid
                });
            });
        });
        
        return transformed;
    };

    // Fetch cached validation results if user navigates directly to step 2
    useEffect(() => {
        const fetchCachedValidations = async () => {
            if (step === 2 && validationData.length === 0) {
                try {
                    const response = await cachedValidationResults(tenderId, 'summary_spreadsheet');
                    const transformedValidations = transformValidationData(response.validations || []);
                    setValidationData(transformedValidations);
                } catch (error) {
                    console.error("Error fetching cached validation results:", error);
                    // Silently fail - user can still proceed
                }
            }
        };

        fetchCachedValidations();
    }, [step, tenderId, validationData.length]);

    // Update handleFileUpload:
    const handleFileUpload = async (selectedFiles) => {
        setLoading(true);
        try {
            const result = await importFinalizedSummary(tenderId, selectedFiles);

            if (result.status !== 200) {
                throw new Error(`Upload failed with status ${result.status}`);
            }
            const data = await result.json();
            setFiles(data.files);

            await handleValidateAfterImport();
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file upload:", error);
            showToast('Ensure correct documents are selected.', 'error');
        } finally {
            setLoading(false);
        }
    };


    const handleValidateAfterImport = async () => {
        try {
            const data = await validateFinalizedSummary(tenderId);

            const transformedValidations = transformValidationData(data.validations || []);
            setValidationData(transformedValidations);
            setStep(2);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during validation:", error);
            showToast('Validation failed. Please check the file.', 'error');
        }
    };

    // Add continue handler:
    const handleContinueAfterValidation = () => {
        setStep(3);
    };

    const handleReselectFiles = async () => {
        setLoading(true);
        try {
            const result = await reselectFinalizedSummarySheet(tenderId);

            if (result.message !== 'Finalized Summary reset successfully') {
                throw new Error('Unexpected response message');
            }
            setFiles([]);
            setValidationData([]);
            setStep(1);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during file reselect:", error);
            showToast('Something went wrong, please try again.', 'error');
        } finally {
            setLoading(false);
        }
    };

    const handleProcessSpreadsheet = async () => {
        setLoading(true);
        try {
            const result = await processSummarySheetPdf(tenderId);

            if (result.message !== 'Finalized Summary processed successfully') {
                throw new Error('Unexpected response message');
            }
            setStep(4);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet processing:", error);
            showToast('An error occurred. Please check documents.', 'error');
        } finally {
            setLoading(false);
        }
    };
    const handleExportSpreadsheet = async () => {
        setLoading(true);        
        try {
            await exportSummarySheetPdf(tenderId);
            navigate(`/${tenderId}/product-comparison`);
        } catch (error) {
            if (error.isAuthExpired) return;
            console.error("An error occurred during spreadsheet export:", error);
            showToast('An error occurred during spreadsheet export.', 'error');
        } finally {
            setLoading(false);
        }
    }

    const finalizedSummarySteps = [
        { id: 1, label: "Upload" },
        { id: 2, label: "Validate" },
        { id: 3, label: "Processed" },
        { id: 4, label: "Generate" },
    ];

    const stepMappings = {
        1: {
            title: "Upload Finalized Summary Breakdown",
            subtitle: "Ensure that your Draft Summary Breakdown is Checked and Verified before importing",
            component: <UploadSourceDocuments handleFileUpload={handleFileUpload} />,
        },
        2: {
            title: "Validate Imported Data",
            subtitle: "Review validation results before processing",
            component: <ValidationReview 
                handleContinue={handleContinueAfterValidation} 
                handleReselect={handleReselectFiles}
                validationData={validationData} 
            />,
        },
        3: {
            title: "Processed Summary Breakdown", 
            subtitle: "Your Summary Sheet is ready to be Generated",
            component: <ReviewFiles handleReselectFiles={handleReselectFiles} handleProcessSpreadsheet={handleProcessSpreadsheet} files={files} />,
        },
        4: {
            title: "Import Complete",
            subtitle: "Your Summary Sheet is ready.",
            component: <SaveSummarySpreadsheet handleExportSpreadsheet={handleExportSpreadsheet} />,
        },
    };

    return (
        <SourceDocumentsPageContainer step={step}>
            <SubStepTracker currentStep={step} customSteps={finalizedSummarySteps} />
            <Header data-testid="tenderHeader">{stepMappings[step].title}</Header>
            <SubHeader data-testid="tenderSubheader">{stepMappings[step].subtitle}</SubHeader>
            {loading ? (
                <img src="/Icons/jigsaw-loading.gif" alt="Loading" data-testid="loading-screen" />
            ) : (
                stepMappings[step].component
            )}
        </SourceDocumentsPageContainer>
    );
};

export default FinalizedSummary;